import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography, IconButton, Tooltip } from "@mui/material";
import { Download, InsertDriveFile, BrokenImage, OpenInNew } from "@mui/icons-material";
import { crmService, LeadMessage } from "../../services/crmService";
import { colors, typography } from "../../theme/designSystem";

// Anexo de uma mensagem de WhatsApp. O arquivo vem do MinIO pela API (blob + object URL),
// porque o storage é interno e <img>/<audio> não mandam header de autenticação.

interface MessageMediaProps {
  message: LeadMessage;
  entityId: string;
}

const isImage = (mime: string | null) => (mime || "").startsWith("image/");
const isAudio = (mime: string | null) => (mime || "").startsWith("audio/");
const isVideo = (mime: string | null) => (mime || "").startsWith("video/");

const MessageMedia: React.FC<MessageMediaProps> = ({ message, entityId }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!message.hasMedia) {
      return;
    }

    let objectUrl: string | null = null;
    let active = true;

    crmService
      .getMessageMedia(message.id, entityId)
      .then((result) => {
        if (!active) {
          // desmontou antes de chegar: revoga aqui senão o blob fica preso na memória da aba
          URL.revokeObjectURL(result);
          return;
        }
        objectUrl = result;
        setUrl(result);
      })
      .catch(() => {
        if (active) {
          setFailed(true);
        }
      });

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [message.id, message.hasMedia, entityId]);

  // Arquivo que a Meta já apagou (7 dias) ou que falhou: dizer o que houve é melhor que
  // mostrar uma mensagem vazia e deixar a recepção procurar defeito onde havia prazo.
  if (!message.hasMedia) {
    return (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.75,
          backgroundColor: "#f8f9fa",
          border: "1px dashed #ced4da",
          borderRadius: "8px",
          p: 1,
          mb: 0.5,
        }}
      >
        <BrokenImage sx={{ fontSize: 18, color: colors.textMuted }} />
        <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
          {message.mediaError || "Arquivo ainda não baixado."}
        </Typography>
      </Box>
    );
  }

  if (failed) {
    return (
      <Typography sx={{ fontSize: typography.fontSize.xs, color: colors.error, mb: 0.5 }}>
        Não foi possível carregar o arquivo.
      </Typography>
    );
  }

  if (!url) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 1.5 }}>
        <CircularProgress size={18} sx={{ color: colors.primary }} />
      </Box>
    );
  }

  if (isImage(message.mimeType)) {
    return (
      <Box
        component="a"
        href={url}
        target="_blank"
        rel="noreferrer"
        sx={{ display: "block", mb: 0.5 }}
      >
        <Box
          component="img"
          src={url}
          alt={message.caption || "Imagem recebida"}
          sx={{ maxWidth: "100%", maxHeight: 260, borderRadius: "8px", display: "block" }}
        />
      </Box>
    );
  }

  if (isAudio(message.mimeType)) {
    return (
      <Box component="audio" controls src={url} sx={{ width: "100%", maxWidth: 260, mb: 0.5 }} />
    );
  }

  if (isVideo(message.mimeType)) {
    return (
      <Box
        component="video"
        controls
        src={url}
        sx={{ maxWidth: "100%", maxHeight: 260, borderRadius: "8px", mb: 0.5 }}
      />
    );
  }

  // PDF o navegador exibe sozinho: abrir vale mais que baixar, e antes só havia download —
  // clicar mandava o arquivo para a pasta de downloads sem nada visível acontecer.
  const canOpenInBrowser = (message.mimeType || "").includes("pdf");

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5, minWidth: 0 }}>
      <InsertDriveFile sx={{ fontSize: 18, color: colors.textSecondary, flexShrink: 0 }} />
      <Typography
        component={canOpenInBrowser ? "a" : "span"}
        {...(canOpenInBrowser ? { href: url, target: "_blank", rel: "noreferrer" } : {})}
        sx={{
          fontSize: typography.fontSize.xs,
          color: canOpenInBrowser ? colors.primary : colors.textPrimary,
          textDecoration: canOpenInBrowser ? "underline" : "none",
          cursor: canOpenInBrowser ? "pointer" : "default",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1,
        }}
      >
        {message.fileName || "Arquivo"}
      </Typography>
      {canOpenInBrowser && (
        <Tooltip title="Abrir em nova aba">
          <IconButton
            component="a"
            href={url}
            target="_blank"
            rel="noreferrer"
            size="small"
            sx={{ flexShrink: 0, width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
          >
            <OpenInNew sx={{ fontSize: 18, color: colors.textSecondary }} />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title="Baixar arquivo">
        <IconButton
          component="a"
          href={url}
          download={message.fileName || "arquivo"}
          size="small"
          sx={{ flexShrink: 0, width: { xs: 40, sm: 32 }, height: { xs: 40, sm: 32 } }}
        >
          <Download sx={{ fontSize: 18, color: colors.textSecondary }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
};

export default MessageMedia;
